import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useParams, useRouteMatch, useHistory } from "react-router-dom";

import {
  createPage,
  updatePageMetadata,
  deletePage,
  updateLandingPageSettings,
  updatePageVisibility,
  invalidatePageDelete,
  cloneLandingPage,
  invalidatePageChange,
  pageSeekIndexing,
  disablePageSeekIndexing
} from "actions/pages";

import _ from "lodash";
import cx from 'classnames';
import Sticky from "react-sticky-el";

import {
  Container,
  TabContainer,
  Row,
  Col,
  Nav,
  Button,
  Tab,
  Form,
  FormCheck
} from "react-bootstrap";

import StoryboardContext from 'components/Storyboard/context';
import { PageItem, SEOBasics, SocialShare, SaveMessageModal, DeleteModal, MultiplePageAlias } from './../../components/index';
import { ImportHtmlButton } from 'pages/pages/components/importHtml';
import { GatingList, GatingForm } from "pages/pages/gating/settings";
import { CopyEmbedCodeButton } from "components/Buttons";
import { HeaderContext } from "pages/pages/components/header";
import { hasUnsavedChanges } from "pages/pages/helper/index";
import { SaveDialog } from "components/Modal";
import storyboardRoleService from "services/storyboardRoles.service";
import { getBaseUrl } from 'services/util';
import { fetchPageAliases, invalidateAliases } from "actions/aliases";
import { resolveCanonicalAlias, withPersistedAlias } from "pages/pages/helper/schemaMarkup";
import { SLIDE_STORYBOARDS } from "constants/storyboardType";
import styles from './settings.module.scss';

export default function LangingPageSettings({ editorSession, defaultParent, onClose, type }) {
  const dispatch = useDispatch();
  const history = useHistory();

  let { path, url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const listReference = React.createRef(null);

  const { id } = useParams();

  const storyboard = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboard, 'type');

  const [state, setState] = useState({
    selected: null,
    selectedGating: null
  })

  const pages = useSelector(state => _.get(state, 'pages.items', []));
  const activePage = useSelector(state => _.get(state, `pages.page[${id}].item`, {}));
  const aliases = useSelector(state => _.get(state, 'aliases.items') || []);
  const persistedAlias = useSelector(state => _.get(state, 'aliases.alias.item'));
  const deletion = useSelector(state => _.get(state, 'pages.deletion', {}));
  const creating = useSelector(state => _.get(state, 'pages.creating', false));

  useEffect(() => {
    if (!activePage) return;
  
    setState(prev => {
      const next = {
        ...prev,
        selected: activePage,
        request: buildRequest(activePage),
        pageInfo: {
          name: _.get(activePage, 'name', ''),
          alias: _.get(activePage, 'canonical', ''),
          autoCreateAlias: _.get(activePage, 'autoCreateAlias'),
        },
        seo: _.get(activePage, 'draft.seo', {}),
        social: _.get(activePage, 'draft.social', {}),
        twitter: _.get(activePage, 'draft.twitter', {})
      };
  
      // 🔒 Prevent re-render loop
      if (_.isEqual(prev, next)) {
        return prev;
      }
  
      return next;
    });
  }, [id, activePage]);

  useEffect(() => {
    const nextAlias = resolveCanonicalAlias(
      activePage,
      withPersistedAlias(aliases, persistedAlias),
    );
    if (!nextAlias) return;

    setState((prev) => {
      if (!prev.request || _.get(prev, 'request.info.alias') === nextAlias) {
        return prev;
      }

      return {
        ...prev,
        request: {
          ...prev.request,
          info: {
            ..._.get(prev, 'request.info'),
            alias: nextAlias,
          },
        },
        pageInfo: {
          ...prev.pageInfo,
          alias: nextAlias,
        },
      };
    });
  }, [aliases, persistedAlias, activePage]);

  useEffect(() => {
    if (deletion && deletion.deleted && _.get(deletion, 'id', null) === id) {
      var nextPage = pages[Math.max(_.get(deletion, 'deletedIndex', 0), 0)];
      if (nextPage) {
        dispatch(invalidatePageChange(nextPage.id));
        history.push(`${baseUrl}/pages/${nextPage.id}`);
      }

      dispatch(invalidatePageDelete());
    }
  }, [deletion])

  const headerContext = useContext(HeaderContext);
  var {
    currentWidgets,
    previousWidgets,
    menuDesigns,
    previousMenuDesigns,
    variableMappings,
    deletedVariableMappings,
    updateState,
    updating,
    entityType,
  } = useSelector(
    (state) => ({
      currentWidgets: _.get(state, `${editorSession}.present.widgets`, []),
      previousWidgets: _.get(state, `pages.page.${id}.item.draft.widgets`, []),
      menuDesigns: _.get(state, `${editorSession}.present.appearance`),
      previousMenuDesigns: _.get(state, `pages.page.${id}.item.storyboard.appearance`),
      variableMappings: _.get(state, `pages.page.${id}.item.variableMappings`, []),
      deletedVariableMappings: _.get(state, `pages.page.${id}.item.deletedVariableMappings`, []),
      updateState: _.get(state, `pages.page.${id}.update`),
      updating: _.get(state, `pages.page.${id}.update.updating`, false),
      entityType: _.get(state, `${editorSession}.present.entityType`),
    }),
    shallowEqual
  );

  const hasChanges = hasUnsavedChanges({
    currentWidgets,
    previousWidgets,
    menuDesigns,
    previousMenuDesigns,
    variableMappings,
    deletedVariableMappings,
  });

  const saveError = updateState?.error;
  const saveSucceeded = updateState?.success === true;
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [waitingForSave, setWaitingForSave] = useState(false);
  const pendingNavigationRef = React.useRef(null);

  const handlePageSave = () => {
    headerContext.onSave();
  };

  useEffect(() => {
    if (!waitingForSave) {
      return;
    }

    if (updating) {
      return;
    }

    if (saveError) {
      setWaitingForSave(false);
      return;
    }

    if (!saveSucceeded && hasChanges) {
      return;
    }

    setWaitingForSave(false);
    setShowSaveModal(false);

    if (pendingNavigationRef.current) {
      pendingNavigationRef.current();
      pendingNavigationRef.current = null;
    }
  }, [waitingForSave, updating, hasChanges, saveError, saveSucceeded]);

  const handlePageClick = (page) => {
    if (id === page.id){
      return;
    }

    const proceedNavigation = () => {
      setState((state) => ({
        ...state,
        selectedGating: null
      }));

      dispatch(invalidatePageChange(page.id));
      history.push(`${baseUrl}/pages/${page.id}`);
    };

    if (hasChanges && storyboardType === "SPATIAL" && entityType === "PAGE") {
      pendingNavigationRef.current = proceedNavigation;
      setShowSaveModal(true);
      return;
    }

    proceedNavigation();
  };

  const handleDeleteClick = (pageId) => {
    dispatch(deletePage(pageId))
  }

  const handleAddPageClick = () => {
    if (!creating) {
      var request = {
        name: `Page ${_.size(pages) + 1}`,
        storyboard: storyboard.id,
        order: _.last(pages).order + 1
      };

    const proceedPageCreation = () => {
      dispatch(
        createPage(request, (response) => {
          history.push(`${baseUrl}/pages/${response.id}`);
        })
      );
    };

    if (hasChanges && storyboardType === "SPATIAL" && entityType === "PAGE") {
      pendingNavigationRef.current = proceedPageCreation;
      setShowSaveModal(true);
      return;
    }

    proceedPageCreation();
  }
};

  const handleClonePage = (pageId) => {
    const proceedCloning = () => {
      dispatch(
        cloneLandingPage(pageId, (response) => {
          history.push(`${baseUrl}/pages/${response.id}`);
        })
      );
    };

    if (hasChanges && storyboardType === "SPATIAL" && entityType === "PAGE") {
      pendingNavigationRef.current = proceedCloning;
      setShowSaveModal(true);
      return;
    }

    proceedCloning();
  }

  // updates the request object of state

  const handlePageChange = (request) => {
    setState((state) => {
      return {
        ...state,
        request: request
      };
    })
  }

  const buildRequest = (page) => {
    if (page == null) {
      return null;
    }

    const request = {
      id: _.get(page, 'id'),
      info: {
        name: _.get(activePage, 'name', ''),
        alias: _.get(activePage, 'canonical', ''),
        autoCreateAlias: _.get(activePage, 'autoCreateAlias'),
      },
      seo: _.get(activePage, 'draft.seo', {}),
      social: _.get(activePage, 'draft.social', {}),
      twitter: _.get(activePage, 'draft.twitter', {})
    };

    return request;
  }

  const validateForm = (request) => {
    const name = _.get(request, 'info.name') || '';
    const alias = _.get(request, 'info.alias') || '';
    const autoCreateAlias = _.get(request, 'info.autoCreateAlias');

    let errors = {};
    var aliasRegex = /^[a-z_-]|[`\s!@#$%^&*()+\=\[\]{};':"\\|,.<>\?~]/;
    var slashRegex = /([^:]\/)\/+/

    if (name.trim() === '') {
      errors['name'] = 'Please provide a valid name';
    }

    if (!autoCreateAlias) {
      if (alias.trim() === '' || aliasRegex.test(alias) || slashRegex.test(alias)) {
        errors['alias'] = 'Please provide valid alias';
      } else if (alias.startsWith('//')) {
        errors['alias'] = 'Alias should begin with /';
      }
    }

    let aliasExists = pages.some(item => {
      var existingAlias = _.get(item, 'properties.desktop.alias');
      return item.uid !== activePage.uid && existingAlias === alias;
    })

    if (aliasExists) {
      errors['alias'] = 'Alias already exist';
    }

    return errors;
  }

  const handleSaveChanges = () => {
    const request = state.request;

    let errors = validateForm(state.request);

    setState(state => {
      return {
        ...state,
        unsavedChanges: false,
        request: {
          ...state.request,
          errors: errors
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      return false;
    }

    const name = _.get(request, 'info.name');
    const alias = resolveCanonicalAlias(
      { ...activePage, canonical: _.get(request, 'info.alias') },
      withPersistedAlias(aliases, persistedAlias),
    );
    const hidden = _.get(request, 'info.hidden');
    const autoCreateAlias = _.get(request, 'info.autoCreateAlias');

    dispatch(updatePageMetadata(id, {
      name: name,
      alias: alias,
      autoCreateAlias: autoCreateAlias,
      hidden: hidden,
      seo: request.seo,
      social: request.social,
      twitter: request.twitter
    }));

    dispatch(updateLandingPageSettings(id, true))

    setState(state => {
      return {
        ...state,
        unsavedChanges: false
      }
    });

    return true;
  }

  const handleResetClick = () => {
    setState((state) => {
      const request = buildRequest(activePage);
      return {
        ...state,
        request: request,
        unsavedChanges: false
      }
    });
  }

  return (
    <section className={styles.pageSettings}>
      <div className={styles.pages} style={{ width: type !== 'page' && '400px' }}>
        <div className={styles.pagesWrapper}>
          <div className={styles.inner} ref={listReference}>
            {pages && pages.map(page => {
              var active = page.id === _.get(activePage, 'id');
              return <PageItem
                key={page.uid}
                editorSession={editorSession}
                page={page}
                pages={pages}
                active={active}
                value={state.request}
                onClick={handlePageClick}
                onClone={handleClonePage}
                onDelete={handleDeleteClick}
                onChange={handlePageChange}
                draggable={false} />
            })}
          </div>
          <Sticky mode='bottom' scrollElement={`.${styles.pagesWrapper}`} boundaryElement={`.${styles.pageSettings}`}>
            <div className={styles.addPageButtonContainer}>
              <Button className={styles.addPageButton} variant='primary' onClick={handleAddPageClick}>
                <i className={cx('cleverstory-plus', styles.icon)} />
                Add Page
              </Button>
              <ImportHtmlButton editorSession={editorSession} onImport={onClose} />
            </div>
          </Sticky>
        </div>
      </div>

      {state.selected && !state.selectedGating && activePage && type === 'page' &&
        <PageSettingForm
          editorSession={editorSession}
          page={activePage}
          value={state.request}
          onChange={handlePageChange}
          onDelete={handleDeleteClick}
          onSave={handleSaveChanges}
          onReset={handleResetClick}
        />}

      <SaveMessageModal
        show={state.unsavedChanges}
        onDiscard={() => {
          handleResetClick();
          if (state.nextPage) {
            handlePageClick(state.nextPage, true);
          }
        }}
        onSave={() => {
          const saved = handleSaveChanges();
          if (saved && state.nextPage) {
            handlePageClick(state.nextPage, true);
          }
        }}
      />
      {showSaveModal && (
        <div onClick={(e) => e.stopPropagation()}>
          <SaveDialog
            isSaving={waitingForSave || updating} 
            error={saveError}
            onCancel={() => {
              setShowSaveModal(false);
              pendingNavigationRef.current = null;
            }}
            onConfirm={() => {
              setWaitingForSave(true);
              handlePageSave();
            }}
          />
        </div>
      )}
    </section>
  )
}

const PageSettingForm = ({ editorSession, page, value, onChange, onDelete, onSave, onReset }) => {
  const dispatch = useDispatch();

  const updating = useSelector(state => _.get(state, `pages.page.${page.id}.update.updating`, false));
  const hiding = useSelector(state => _.get(state, `pages.pageHiding`, false));

  const [state, setState] = useState({
    active: 'info'
  })

  const handleMenuChange = (active) => {
    setState(state => {
      return {
        ...state,
        active: active
      }
    })
  }

  useEffect(() => {
    if (page && page.id) {
      setState(state => {
        return {
          ...state,
          active: 'info'
        }
      })
    }
  }, [page && page.id]);

  useEffect(() => {
    const pageId = _.get(page, 'id');
    if (!pageId) return undefined;

    dispatch(fetchPageAliases({ type: 'page', target: pageId }, 0, 20));

    return () => {
      dispatch(invalidateAliases());
    };
  }, [page?.id, dispatch]);

  const handleInfoChange = (change) => {
    const request = {
      ...value,
      info: {
        ...value.info,
        ...change
      }
    };

    onChange(request);
  }

  const handleSEOChange = (change) => {
    const request = {
      ...value,
      seo: {
        ...value.seo,
        ...change
      }
    };

    onChange(request);
  }

  const handleSocialChange = (type, change) => {
    const request = {
      ...value,
      [type]: {
        ...value[type],
        ...change
      }
    };

    onChange(request);
  }

  return (
    <div className={cx(styles.pageSettingForm, { [styles.maxWidth]: state.active == 'manageAlias' })}>
      <Container fluid>
        <TabContainer defaultActiveKey={'info'} activeKey={state.active} onSelect={handleMenuChange}>
          <Row>
            <Col sm={state.active == 'manageAlias'?2:3} className={styles.options}>
              <Nav className="flex-column">
                <Nav.Item>
                  <Nav.Link eventKey="info">Page Info</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="seo">SEO Basics</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="social">Social Share</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="manageAlias">Manage Aliases</Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>
            <Col sm={state.active == 'manageAlias'?10:9} className={styles.pageSettingsDetail}>
              <Tab.Content>
                <Tab.Pane eventKey="info">
                  <PageInfo editorSession={editorSession} page={page} value={value} onChange={handleInfoChange} onDelete={onDelete} />
                </Tab.Pane>
                <Tab.Pane eventKey="seo">
                  <SEOBasics editorSession={editorSession} page={page} value={value} onChange={handleSEOChange} />
                </Tab.Pane>
                <Tab.Pane eventKey="social">
                  <SocialShare editorSession={editorSession} page={page} value={value} onChange={handleSocialChange} />
                </Tab.Pane>
                <Tab.Pane eventKey="manageAlias">
                  <MultiplePageAlias active={state.active} editorSession={editorSession} page={page} value={value} />
                </Tab.Pane>
                {state.active !== "manageAlias" &&
                  <footer className={styles.buttonContainer}>
                    <Button variant="secondary" type="reset" disabled={hiding} onClick={onReset}>Reset</Button>
                    <Button variant="primary" type="submit" disabled={updating || hiding} onClick={onSave}>{updating ? 'Saving' : 'Save Changes'}</Button>
                  </footer>
                }
              </Tab.Content>
            </Col>
          </Row>
        </TabContainer>
      </Container>
    </div>
  )
}

const PageInfo = ({ page, value, onChange, onDelete }) => {
  const dispatch = useDispatch()
  const [deleteModal, showDeleteModal] = useState(false)
  const [selectedDomain,setSelectedDomain] = useState({})

  const errors = _.get(value, 'errors') || {};
  const autoCreateAlias = _.get(value, 'info.autoCreateAlias');
  const id = _.get(page, 'id', null)

  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));
  const pages = useSelector(state => _.get(state, 'pages.items', []));
  const currentPage = useSelector(state => _.get(state, `pages.page[${id}].item`, []));
  const seekEnabled = _.get(currentPage, 'seekEnabled', false);
  const seekStatus = _.get(currentPage, 'seekStatus');
  const hidden = _.get(currentPage, 'hidden', false);
  const seekEnabledWorkspace = useSelector(state => { return _.get(state, 'workspaces.workspace.item.seekSettings.seekEnabled', false) });

  var domain = useSelector(state => _.get(state, 'workspaces.workspace.item.defaultDomain') || "");

  useEffect(()=>{
    let domainUrl = _.get(storyboard,'domain') || domain;
    setSelectedDomain(domainUrl);
  },[storyboard, domain])

  const handleNameChange = (e) => {
    const value = e.target.value;
    onChange({
      name: value
    });
  }

  const toggleHide = () => {
    dispatch(updatePageVisibility(id, !hidden))
  }

  const toggleSeekIndexing = () => {
    const request = {
      contentType: 'page',
      content: id,
      contentMapping: null,
      storyboard: _.get(storyboard, 'id'),
      addStoryboard: !seekEnabled,
      seekEnabled: seekEnabled,
      seekStatus: seekStatus
    };

    dispatch(pageSeekIndexing(id, request));
  }

  const seekActivation =()=> {
    switch (true) {
      case (seekEnabled || (seekStatus == 'PROCESSING' || seekStatus == 'REQUESTED')):
        return true;
    
      case (!seekEnabled && (seekStatus == 'COMPLETED' || seekStatus == null)):
        return false;
    }
  }

  if(!currentPage) {
    return null;
  }

  return (
    <div className={styles.pageInfo}>
      <div className={styles.headerContainer}>
        <div>
          <h3 className={styles.header}>Page Info</h3>
        </div>
        <div>
          <button className={styles.deleteButton}>
            {(pages && pages.length > 1) && storyboardRoleService.canManageStoryboard(storyboard) && <i className={cx('cleverstory-trash', styles.deleteIcon)} onClick={() => showDeleteModal(true)} />}
          </button>
        </div>
        {<DeleteModal deleteModal={deleteModal} showDeleteModal={showDeleteModal} onDelete={onDelete} page={page} pages={pages} />}
      </div>
      <Form className={styles.form}>
        <Form.Group controlId="pageName" className={styles.group}>
          <Form.Label className={styles.label}>What&apos;s this page name?</Form.Label>
          <Form.Control isInvalid={errors.name}
            className={styles.control}
            type="text"
            name="name"
            placeholder="Enter page name"
            value={_.get(value, 'info.name', '')}
            onChange={handleNameChange} />
          <Form.Control.Feedback type="invalid">
            {errors.name || 'please provide a valid name'}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group controlId="indexing" className={cx(styles.smallGroup, styles.flexFormGroup)}>
          <Form.Label className={styles.inlineLabel}>Hide this page</Form.Label>
          <Form.Check
            type="switch"
            className={styles.switchButton}
          >
            <FormCheck.Input isvalid onChange={toggleHide} checked={hidden} />
          </Form.Check>
        </Form.Group>

      {seekEnabledWorkspace && currentPage.published && !SLIDE_STORYBOARDS.includes(_.get(storyboard, 'type')) && (
        <Form.Group controlId="seekIndexing" className={cx(styles.smallGroup, styles.flexFormGroup)}>
          <Form.Label className={styles.inlineLabel}>Index for seek</Form.Label>
          {(seekStatus == "PROCESSING" ||
              seekStatus == "REQUESTED") && (
                <div className={styles.loader}>
                  <span className={styles.text}>Indexing...</span>
                    <Button variant="info" onClick={toggleSeekIndexing}>
                      <i className={cx("cleverstory-sync")} />
                    </Button>
                </div>
          )}
          <Form.Check
            type="switch"
            className={styles.switchButton}
          >
            <FormCheck.Input disabled={seekStatus == "PROCESSING" || seekStatus == "REQUESTED"} isvalid onMouseDown={toggleSeekIndexing} checked={seekActivation()} />
          </Form.Check>
        </Form.Group> 
      )}

        {(currentPage.published && !SLIDE_STORYBOARDS.includes(_.get(storyboard, 'type'))) &&
          <CopyEmbedCodeButton id={id} domain={_.get(selectedDomain, 'url')} height={815}>
            <Form.Group controlId="copyEmbedCode" className={cx(styles.smallGroup, styles.flexFormGroup)}>
            <Form.Label className={styles.copyCode}>
              <i className={cx("cleverstory-Embed")} /> Copy Embed Code
            </Form.Label>

            </Form.Group>
          </CopyEmbedCodeButton>
        }
      </Form>
    </div>
  )
}